import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import AlignDiagram from "@/components/AlignDiagram";

export default function Align() {
  return (
    <main data-testid="page-align" className="pt-20">
      {/* HERO — tighter, single column, more breathing room */}
      <section className="relative overflow-hidden bg-[var(--navy-950)] text-[var(--bone)]">
        <div className="grain absolute inset-0"></div>
        <div
          className="absolute -top-32 -right-40 w-[700px] h-[700px] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(30,90,176,0.35), transparent 60%)" }}
        />
        <div
          className="absolute -bottom-40 -left-32 w-[600px] h-[600px] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(201,164,90,0.18), transparent 60%)" }}
        />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-28 pb-24 lg:pt-36 lg:pb-32">
          <div className="eyebrow-light rise rise-1">Our Signature Framework</div>
          <h1 className="font-display text-[52px] md:text-[80px] lg:text-[104px] leading-[0.95] mt-6 rise rise-2 max-w-5xl">
            A.L.I.G.N.
            <span className="text-[var(--gold-soft)] align-super text-[28px] md:text-[36px] ml-2">™</span>
          </h1>
          <p className="mt-10 max-w-3xl text-[19px] md:text-[22px] leading-[1.5] text-[var(--navy-100)]/90 rise rise-3">
            A practical systems model for aligning leadership, operations,
            AI adoption, and workforce readiness — built to turn complexity
            into clear, measurable execution.
          </p>

          <div className="hairline-light mt-16"></div>
          <div className="mt-6 flex flex-wrap items-center gap-x-8 gap-y-3 text-[11px] tracking-[0.28em] uppercase text-[var(--navy-100)]/60">
            <span>Leadership</span>
            <span className="text-[var(--gold-soft)]">·</span>
            <span>Workforce Readiness</span>
            <span className="text-[var(--gold-soft)]">·</span>
            <span>Operations</span>
            <span className="text-[var(--gold-soft)]">·</span>
            <span>AI Adoption</span>
            <span className="text-[var(--gold-soft)]">·</span>
            <span>Future Workforce</span>
          </div>
        </div>
      </section>

      {/* DIAGRAM — centerpiece, with tightened intro */}
      <section className="bg-[var(--bone)] py-24 lg:py-32 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-5">
            <div className="eyebrow">Freedom In Me Systems Alignment Model</div>
            <h2 className="font-display text-[40px] md:text-[52px] leading-[1.04] text-[var(--navy-900)] mt-5">
              Five systems.<br />
              One aligned organization.
            </h2>
            <p className="mt-6 text-[16px] leading-[1.7] text-[var(--muted)] max-w-md">
              A.L.I.G.N.™ moves five integrated systems around a single center
              of gravity: <span className="text-[var(--navy-900)] font-semibold">measurable execution</span>.
              Each pillar performs only when connected back to the whole.
            </p>
            <div className="mt-8 space-y-3">
              {[
                "Systems-based, not symptom-based",
                "Built for leaders, operators, and modern workforces",
                "Outcome-scoped, never billed by the hour",
              ].map((t) => (
                <div key={t} className="flex items-start gap-3">
                  <CheckCircle2 size={18} className="text-[var(--gold)] mt-0.5 flex-shrink-0" />
                  <span className="text-[15px] text-[var(--navy-900)]">{t}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <AlignDiagram />
          </div>
        </div>
      </section>

      {/* TRANSFORMATION FLOW — clean four-step */}
      <section className="bg-[var(--navy-50)] py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="eyebrow">From Diagnosis To Execution</div>
            <h2 className="font-display text-[40px] md:text-[52px] leading-[1.05] text-[var(--navy-900)] mt-5">
              A clear path. Four steps. Measurable outcomes.
            </h2>
          </div>

          <div className="mt-16 grid md:grid-cols-4 gap-px bg-[rgba(10,31,68,0.08)]">
            {[
              { n: "01", t: "Organizational Assessment", d: "Diagnose what's breaking and why — across all five systems." },
              { n: "02", t: "Strategy & System Design",  d: "Build the plan, structure, and scope to the outcomes you need." },
              { n: "03", t: "Implementation & Training", d: "Equip leaders and teams to execute with consistency." },
              { n: "04", t: "Advisory & Retainer",       d: "Sustain alignment as conditions, AI, and workforce evolve." },
            ].map((s) => (
              <div key={s.n} className="bg-[var(--bone)] p-8 lg:p-10">
                <div className="font-display text-[40px] text-[var(--gold)] leading-none">{s.n}</div>
                <div className="font-display text-[20px] text-[var(--navy-900)] mt-5 leading-tight">{s.t}</div>
                <p className="mt-3 text-[14px] leading-[1.6] text-[var(--muted)]">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING — quiet, confident, two clear next steps */}
      <section className="relative overflow-hidden bg-[var(--navy-950)] text-[var(--bone)]">
        <div className="grain absolute inset-0"></div>
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-24 lg:py-32 text-center">
          <div className="eyebrow-light">Begin With Clarity</div>
          <h2 className="font-display text-[40px] md:text-[58px] leading-[1.05] mt-5">
            Bring A.L.I.G.N.<span className="text-[var(--gold-soft)] align-super text-[20px]">™</span> into your organization.
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-[16px] text-[var(--navy-100)]/85">
            A 30-minute call to pinpoint the gaps — and show what alignment
            could unlock across leadership, operations, AI, and your workforce.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <a
              href="https://www.itsthefreedominme.com"
              target="_blank"
              rel="noreferrer"
              data-testid="align-cta-book-call"
              className="btn-gold px-8 py-4 rounded-full text-[12px] tracking-[0.22em] uppercase font-semibold inline-flex items-center gap-2"
            >
              Book a Strategy Call <ArrowUpRight size={14} />
            </a>
            <Link
              to="/assessment"
              data-testid="align-cta-assessment"
              className="px-8 py-4 rounded-full text-[12px] tracking-[0.22em] uppercase font-semibold border border-[var(--gold-soft)] text-[var(--gold-soft)] hover:bg-[var(--gold)] hover:text-[var(--navy-950)] inline-flex items-center gap-2 transition-colors"
            >
              Take the Readiness Index
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
